import React, { useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes
} from "react-router-dom";
import ReactGA from "react-ga4";

import AppLayouts from "layouts/AppLayout";
import PageNotFound from "components/PageNotFound/PageNotFound";

import "styles/common.scss";

import { services as servicesData } from "containers/services/servicesData";
import { caseStudies as caseStudiesData } from "containers/caseStudies/caseStudiesData";

const Home = lazy(() => import("containers/home/index"));
const Contact = lazy(() => import("containers/contact/index"));
const ThankYou = lazy(() => import("containers/thankYou/index"));
const ServiceDetail = lazy(() => import("containers/services/ServiceDetail"));
const CaseStudies = lazy(() => import("containers/caseStudies/CaseStudies"));
const CaseStudyDetail = lazy(
  () => import("containers/caseStudies/CaseStudyDetail")
);

function App() {
  useEffect(() => {
    const timer = setTimeout(() => {
      ReactGA.initialize(process.env.REACT_APP_GOOGLE_TAG_KEY); //eslint-disable-line
      ReactGA.send({ hitType: "pageview", page: window.location.pathname });
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Router>
      <Suspense fallback={<div></div>}>
        <Routes>
          <Route
            path="/"
            element={
              <AppLayouts>
                <Home />
              </AppLayouts>
            }
          />
          <Route
            path="/contact"
            element={
              <AppLayouts>
                <Contact />
              </AppLayouts>
            }
          />
          <Route
            path="/thank-you"
            element={
              <AppLayouts>
                <ThankYou />
              </AppLayouts>
            }
          />
          {/* Phase 2 service detail pages under /services/*, one route per
                entry in servicesData, all rendered by the ServiceDetail
                template. */}
          {Object.values(servicesData).map((service) => (
            <Route
              key={service.slug}
              path={`/services/${service.slug}`}
              element={
                <AppLayouts>
                  <ServiceDetail service={service} />
                </AppLayouts>
              }
            />
          ))}

          {/* Case studies: index + one detail route per study. */}
          <Route
            path="/case-studies"
            element={
              <AppLayouts>
                <CaseStudies />
              </AppLayouts>
            }
          />
          {Object.values(caseStudiesData).map((caseStudy) => (
            <Route
              key={caseStudy.slug}
              path={`/case-studies/${caseStudy.slug}`}
              element={
                <AppLayouts>
                  <CaseStudyDetail caseStudy={caseStudy} />
                </AppLayouts>
              }
            />
          ))}

          {/* Retired Agri CTRM pages and their legacy camelCase URLs now
                redirect to the merged /services/* pages. The .htaccess 301s
                handle direct hits and SEO; these cover in-app navigation and
                hosts without mod_rewrite. */}
          <Route
            path="/system-development"
            element={
              <Navigate to="/services/custom-software-development" replace />
            }
          />
          <Route
            path="/systemDevelopment"
            element={
              <Navigate to="/services/custom-software-development" replace />
            }
          />
          <Route
            path="/system-integration"
            element={
              <Navigate to="/services/enterprise-system-integration" replace />
            }
          />
          <Route
            path="/systemIntegration"
            element={
              <Navigate to="/services/enterprise-system-integration" replace />
            }
          />
          <Route
            path="/production-support"
            element={
              <Navigate to="/services/production-support-maintenance" replace />
            }
          />
          <Route
            path="/productionSupport"
            element={
              <Navigate to="/services/production-support-maintenance" replace />
            }
          />

          <Route
            path="/*"
            element={
              <AppLayouts headerRequired={false} footerRequired={false}>
                <PageNotFound />
              </AppLayouts>
            }
          />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
